<script lang="ts">
  import { onMount } from 'svelte';
  import type { ClientUserSummary, CreateClientUserRequest, TenantSummary } from '@ucc/shared';
  import {
    ApiError,
    createClientUser,
    fetchClientUsers,
    fetchTenants,
    resetClientPassword,
    updateClientUser,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let users = $state<ClientUserSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let actionError = $state<string | null>(null);
  let actionOk = $state<string | null>(null);
  let creating = $state(false);
  let togglingId = $state<string | null>(null);

  let q = $state('');
  let tenantFilter = $state('all');
  let statusFilter = $state<'all' | 'active' | 'disabled'>('all');

  let email = $state('');
  let displayName = $state('');
  let tenantId = $state('');

  const isAdmin = $derived(session.user?.role === 'admin');
  const isStaff = $derived(session.user?.role === 'admin' || session.user?.role === 'user');
  const meId = $derived(session.user?.id ?? '');

  const filtered = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (tenantFilter !== 'all' && u.tenantId !== tenantFilter) return false;
      const disabled = !!u.disabledAt;
      if (statusFilter === 'active' && disabled) return false;
      if (statusFilter === 'disabled' && !disabled) return false;
      if (!needle) return true;
      return (
        u.email.toLowerCase().includes(needle) ||
        u.displayName.toLowerCase().includes(needle) ||
        u.tenantName.toLowerCase().includes(needle)
      );
    });
  });

  const activeCount = $derived(users.filter((u) => !u.disabledAt).length);
  const disabledCount = $derived(users.filter((u) => !!u.disabledAt).length);

  function fmtWhen(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const [uRes, tRes] = await Promise.all([fetchClientUsers(), fetchTenants()]);
      users = uRes.users;
      tenants = tRes.tenants;
      if (!tenantId) {
        if (!isAdmin && session.user?.tenantId) {
          tenantId = session.user.tenantId;
        } else if (tenants[0]) {
          tenantId = tenants[0].id;
        }
      }
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load clients';
    } finally {
      loading = false;
    }
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    actionError = null;
    actionOk = null;
    const em = email.trim().toLowerCase();
    const name = displayName.trim();
    const tid = isStaff ? tenantId.trim() : (session.user?.tenantId ?? '');
    if (!em || !name) {
      actionError = 'Email and display name are required.';
      return;
    }
    if (!tid) {
      actionError = 'Tenant is required.';
      return;
    }
    creating = true;
    try {
      const body: CreateClientUserRequest = {
        email: em,
        displayName: name,
        tenantId: tid,
      };
      const res = await createClientUser(body);
      users = [...users, res.user].sort((a, b) => a.email.localeCompare(b.email));
      actionOk = res.emailSent
        ? `Invite sent to ${res.user.email}. They must activate within 72 hours.`
        : `Client created; invite email could not be sent${res.emailError ? ` (${res.emailError})` : ''}.`;
      email = '';
      displayName = '';
          } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Create failed';
    } finally {
      creating = false;
    }
  }

  async function onToggle(u: ClientUserSummary) {
    actionError = null;
    actionOk = null;
    const wantDisabled = !u.disabledAt;
    togglingId = u.id;
    try {
      const res = await updateClientUser(u.id, { disabled: wantDisabled });
      users = users.map((x) => (x.id === u.id ? res.user : x));
      actionOk = wantDisabled ? `Disabled ${u.email}` : `Enabled ${u.email}`;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Update failed';
    } finally {
      togglingId = null;
    }
  }

  async function onResetPassword(u: ClientUserSummary) {
    if (u.disabledAt) {
      actionError = 'Enable the account before sending a reset link.';
      return;
    }
    if (
      !window.confirm(
        u.activatedAt
          ? `Send password reset email to ${u.email}?`
          : `Resend activation invite to ${u.email}?`,
      )
    ) {
      return;
    }
    actionError = null;
    actionOk = null;
    togglingId = u.id;
    try {
      const res = await resetClientPassword(u.id);
      actionOk = res.emailSent
        ? u.activatedAt
          ? `Reset email sent to ${u.email}`
          : `Invite re-sent to ${u.email}`
        : `Link created but email failed${res.emailError ? ` (${res.emailError})` : ''}`;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to send reset email';
    } finally {
      togglingId = null;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <header class="head">
    <div>
      <p class="eyebrow">Access</p>
      <h2>Clients</h2>
      <p class="lede">Tenant-bound client portal accounts. Staff can create and disable clients across tenants.</p>
    </div>
    <div class="head-actions">
      <button type="button" class="linkish" onclick={() => void load()} disabled={loading}>
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  </header>

  <div class="kpis">
    <div class="kpi">
      <span class="kpi-label">Clients</span>
      <strong>{users.length}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Active</span>
      <strong>{activeCount}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Disabled</span>
      <strong>{disabledCount}</strong>
    </div>
  </div>

  <Card title="Create client" subtitle="Bound to a tenant · sends 72-hour activation invite">
    <form class="create" onsubmit={onCreate}>
      <label class="field">
        <span>Email</span>
        <input type="email" bind:value={email} autocomplete="off" required />
      </label>
      <label class="field">
        <span>Display name</span>
        <input type="text" bind:value={displayName} autocomplete="off" required />
      </label>
      {#if isStaff}
        <label class="field">
          <span>Tenant</span>
          <select bind:value={tenantId} required>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
      {:else}
        <label class="field">
          <span>Tenant</span>
          <input type="text" value={session.user?.tenantName ?? 'Your tenant'} disabled />
        </label>
      {/if}

      <div class="form-actions">
        <Button type="submit" variant="primary" disabled={creating || loading}>
          {creating ? 'Creating…' : 'Create client'}
        </Button>
      </div>
    </form>
    {#if actionError}
      <p class="banner error" role="alert">{actionError}</p>
    {/if}
    {#if actionOk}
      <p class="banner ok" role="status">{actionOk}</p>
    {/if}
  </Card>

  <Card title="Client list" subtitle={`${filtered.length} shown`} padded={false}>
    <div class="toolbar">
      <label class="field grow">
        <span class="sr-only">Search</span>
        <input type="search" placeholder="Search email, name, tenant…" bind:value={q} />
      </label>
      {#if isStaff}
        <label class="field">
          <span class="sr-only">Tenant</span>
          <select bind:value={tenantFilter}>
            <option value="all">All tenants</option>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
      {/if}
      <label class="field">
        <span class="sr-only">Status</span>
        <select bind:value={statusFilter}>
          <option value="all">All status</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>
    </div>

    {#if error}
      <p class="banner error" role="alert">{error}</p>
    {:else if loading}
      <p class="banner muted">Loading clients…</p>
    {:else if filtered.length === 0}
      <p class="banner muted">No clients match.</p>
    {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              {#if isStaff}<th>Tenant</th>{/if}
              <th>Status</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each filtered as u (u.id)}
              <tr class:disabled-row={!!u.disabledAt}>
                <td class="mono">{u.email}</td>
                <td>{u.displayName}</td>
                {#if isStaff}
                  <td>{u.tenantName}</td>
                {/if}
                <td>
                  {#if u.disabledAt}
                    <Badge tone="danger">Disabled</Badge>
                  {:else if !u.activatedAt}
                    <Badge tone="warning">Pending</Badge>
                  {:else}
                    <Badge tone="success">Active</Badge>
                  {/if}
                </td>
                <td class="muted">{fmtWhen(u.createdAt)}</td>
                <td class="actions">
                  <Button
                    type="button"
                    size="sm"
                    variant={u.disabledAt ? 'secondary' : 'danger'}
                    disabled={togglingId === u.id || u.id === meId}
                    onclick={() => void onToggle(u)}
                  >
                    {#if togglingId === u.id}
                      …
                    {:else if u.disabledAt}
                      Enable
                    {:else}
                      Disable
                    {/if}
                  </Button>
                  {#if !u.disabledAt && u.id !== meId}
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={togglingId === u.id}
                      onclick={() => void onResetPassword(u)}
                    >
                      {#if togglingId === u.id}
                        …
                      {:else if u.activatedAt}
                        Reset password
                      {:else}
                        Resend invite
                      {/if}
                    </Button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </Card>
</div>

<style>
  .page {
    display: grid;
    gap: var(--space-5, 1.25rem);
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .eyebrow {
    margin: 0 0 0.25rem;
    font-size: var(--text-xs, 0.75rem);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-muted);
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .lede {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
    max-width: 42rem;
  }

  .head-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .linkish {
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text);
    border-radius: 0.5rem;
    padding: 0.4rem 0.75rem;
    font-size: var(--text-sm, 0.9rem);
    cursor: pointer;
  }

  .linkish:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
    gap: 0.75rem;
  }

  .kpi {
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
    background: var(--surface);
    display: grid;
    gap: 0.2rem;
  }

  .kpi-label {
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
  }

  .kpi strong {
    font-size: 1.25rem;
  }

  .create {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0.75rem 1rem;
    align-items: end;
  }

  .form-actions {
    display: flex;
    align-items: end;
  }

  .field {
    display: grid;
    gap: 0.3rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .field span {
    color: var(--text-muted);
    font-weight: 500;
  }

  .field input,
  .field select {
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    padding: 0.45rem 0.6rem;
    background: var(--surface);
    color: inherit;
    font: inherit;
    min-width: 0;
  }

  .field.grow {
    min-width: 12rem;
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  .banner {
    margin: 0;
    padding: 0.85rem 1rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
    background: color-mix(in srgb, var(--danger, #ef4444) 10%, transparent);
  }

  .banner.ok {
    color: var(--success, #047857);
    background: color-mix(in srgb, var(--success, #10b981) 12%, transparent);
  }

  .banner.muted {
    color: var(--text-muted);
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm, 0.9rem);
  }

  th,
  td {
    text-align: left;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
  }

  th {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85em;
  }

  .muted {
    color: var(--text-muted);
    white-space: nowrap;
  }

  .actions {
    text-align: right;
    white-space: nowrap;
  }

  tr.disabled-row td {
    opacity: 0.72;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
</style>
